import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, ArrowRight, Save, Loader2, User, Building, Mail, Phone, MapPin,
    Calendar, CreditCard, FileText, AlertCircle, Check, Globe, Upload, X,
    Briefcase, UserCheck, Building2, Shield, CheckCircle, Clock, XCircle,
    ChevronRight
} from 'lucide-react';
import { clientService } from '@services/clientService';
import { companyService } from '@services/companyService';
import { kycService } from '@services/kycService';
import CustomSelect from '../shared/CustomSelect';

const STATUS_CONFIG = {
    APPROVED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
    VERIFIED: { color: 'bg-blue-100 text-blue-800', icon: Shield, label: 'Verified' },
    REJECTED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
    PENDING:  { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
};

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024;

const STEPS = [
    { id: 'details', label: 'Client Details', icon: User },
    { id: 'company', label: 'Select Company', icon: Building2 },
    { id: 'kyc', label: 'KYC Documents', icon: Shield },
];

const ClientOnboardingWizard = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // Wizard state
    const [currentStep, setCurrentStep] = useState(0);
    const [createdClient, setCreatedClient] = useState(null);

    // Step 1: Client details
    const [activeTab, setActiveTab] = useState('basic');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [formData, setFormData] = useState({
        clientType: 'RETAIL',
        firstName: '', middleName: '', lastName: '',
        dateOfBirth: '', gender: '', nationalId: '', kraPin: '', passportNumber: '',
        email: '', email2: '', phoneNumber: '', phoneNumber2: '',
        address: '', city: '', country: '',
        companyName: '', registrationNumber: '', taxIdNumber: '',
        industry: '', contactPersonName: '', contactPersonEmail: '', contactPersonPhone: '',
        crossSellingConsent: false,
    });

    // Step 2: Company selection
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);

    // Step 3: KYC checklist
    const [checklist, setChecklist] = useState(null);
    const [loadingChecklist, setLoadingChecklist] = useState(false);
    const [uploadingItem, setUploadingItem] = useState(null); // requirementId being uploaded
    const [uploadForm, setUploadForm] = useState({ file: null, documentNumber: '', notes: '' });
    const [dragging, setDragging] = useState(false);
    const [submittingKyc, setSubmittingKyc] = useState(false);
    const [kycError, setKycError] = useState('');
    const [kycSuccess, setKycSuccess] = useState('');

    // Fetch companies when reaching step 2
    useEffect(() => {
        if (currentStep === 1) {
            fetchCompanies();
        }
    }, [currentStep]);

    // Fetch checklist when reaching step 3
    useEffect(() => {
        if (currentStep === 2 && createdClient && selectedCompanyId) {
            fetchChecklist();
        }
    }, [currentStep, selectedCompanyId]);

    const fetchCompanies = async () => {
        try {
            setLoadingCompanies(true);
            const response = await companyService.getAll();
            const data = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
            setCompanies(data.filter(c => c.active));
        } catch (err) {
            console.error('Error fetching companies:', err);
        } finally {
            setLoadingCompanies(false);
        }
    };

    const fetchChecklist = async () => {
        try {
            setLoadingChecklist(true);
            setKycError('');
            const response = await kycService.clientKyc.getChecklist(createdClient.id, selectedCompanyId);
            if (response.success && response.data) {
                setChecklist(response.data);
            }
        } catch (err) {
            console.error('Error fetching checklist:', err);
            setKycError('Failed to load KYC checklist');
        } finally {
            setLoadingChecklist(false);
        }
    };

    // ==================== Step 1: Client Form Logic ====================
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const getFieldTab = (field) => {
        const contactFields = ['email', 'email2', 'phoneNumber', 'phoneNumber2', 'address', 'city', 'country'];
        const corporateFields = ['registrationNumber', 'taxIdNumber', 'contactPersonName', 'contactPersonEmail', 'contactPersonPhone'];
        if (contactFields.includes(field)) return 'contact';
        if (corporateFields.includes(field)) return 'corporate';
        return 'basic';
    };

    const validateStep1 = () => {
        const newErrors = {};
        if (formData.clientType === 'RETAIL') {
            if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
            if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
            if (!formData.email.trim()) newErrors.email = 'Email is required';
            if (!formData.gender) newErrors.gender = 'Gender is required';
        } else {
            if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
            if (!formData.email.trim()) newErrors.email = 'Email is required';
            if (!formData.registrationNumber.trim()) newErrors.registrationNumber = 'Registration number is required';
            if (!formData.contactPersonName.trim()) newErrors.contactPersonName = 'Contact person name is required';
            if (!formData.contactPersonEmail.trim()) newErrors.contactPersonEmail = 'Contact person email is required';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        setErrors(newErrors);

        // If validation fails, switch to the first tab that has errors
        if (Object.keys(newErrors).length > 0) {
            const firstErrorField = Object.keys(newErrors)[0];
            const errorTab = getFieldTab(firstErrorField);
            setActiveTab(errorTab);

            // Show which tabs have errors
            const errorTabs = [...new Set(Object.keys(newErrors).map(getFieldTab))];
            const tabLabels = { basic: 'Basic Information', contact: 'Contact Details', corporate: 'Corporate Details' };
            const tabNames = errorTabs.map(t => tabLabels[t] || t).join(', ');
            setShowErrorMessage(true);
            setErrorMessage(`Please fill in required fields in: ${tabNames}`);
        }

        return Object.keys(newErrors).length === 0;
    };

    const handleCreateClient = async () => {
        if (!validateStep1()) return;
        try {
            setLoading(true);
            setShowErrorMessage(false);
            let response;
            if (formData.clientType === 'RETAIL') {
                response = await clientService.createRetail(formData);
            } else {
                response = await clientService.createCorporate(formData);
            }
            if (response.success && response.data) {
                setCreatedClient(response.data);
                setShowSuccessMessage(true);
                setTimeout(() => {
                    setShowSuccessMessage(false);
                    setCurrentStep(1);
                }, 1000);
            } else {
                setShowErrorMessage(true);
                setErrorMessage(response.message || 'Failed to create client');
            }
        } catch (err) {
            setShowErrorMessage(true);
            setErrorMessage(err.message || 'Failed to create client');
        } finally {
            setLoading(false);
        }
    };

    // ==================== Step 3: KYC Upload Logic ====================
    const validateAndSetFile = (selected) => {
        if (!selected) return;
        if (!ACCEPTED_TYPES.includes(selected.type)) {
            setKycError('Only PDF, JPG, JPEG, and PNG files are accepted');
            return;
        }
        if (selected.size > MAX_SIZE) {
            setKycError('File size must be less than 10MB');
            return;
        }
        setUploadForm(prev => ({ ...prev, file: selected }));
        setKycError('');
    };

    const handleKycSubmit = async (requirementId) => {
        if (!uploadForm.file) return;
        try {
            setSubmittingKyc(true);
            setKycError('');
            const response = await kycService.clientKyc.submitWithFile(
                createdClient.id, requirementId, uploadForm.file,
                uploadForm.documentNumber, uploadForm.notes
            );
            if (response.success) {
                setKycSuccess('Document uploaded successfully');
                setUploadingItem(null);
                setUploadForm({ file: null, documentNumber: '', notes: '' });
                setTimeout(() => setKycSuccess(''), 3000);
                fetchChecklist();
            } else {
                setKycError(response.message || 'Failed to upload document');
            }
        } catch (err) {
            setKycError(err.message || 'Failed to upload document');
        } finally {
            setSubmittingKyc(false);
        }
    };

    const getAutoDocumentNumber = (kycType) => {
        if (!createdClient) return '';
        const typeMap = {
            'NATIONAL_ID': createdClient.nationalId,
            'KRA_PIN': createdClient.kraPin,
            'PASSPORT': createdClient.passportNumber,
            'TAX_REGISTRATION': createdClient.taxIdNumber,
        };
        return typeMap[kycType] || '';
    };

    const openUploadForItem = (requirementId, kycType) => {
        if (uploadingItem === requirementId) {
            setUploadingItem(null);
            setUploadForm({ file: null, documentNumber: '', notes: '' });
        } else {
            setUploadingItem(requirementId);
            const autoNumber = getAutoDocumentNumber(kycType);
            setUploadForm({ file: null, documentNumber: autoNumber, notes: autoNumber ? 'Auto-filled from client details' : '' });
        }
        setKycError('');
    };

    // ==================== Render ====================
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/clients')}
                            className="p-2 hover:bg-gray-100 rounded-lg transition"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-900">New Client</h2>
                            <p className="text-sm text-gray-500 mt-0.5">Create client and set up KYC documents</p>
                        </div>
                    </div>
                    {createdClient && (
                        <span className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full flex items-center gap-1.5">
                            <Check className="w-4 h-4" />
                            {createdClient.clientCode}
                        </span>
                    )}
                </div>
            </div>

            {/* Step Indicator */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                    {STEPS.map((step, index) => {
                        const StepIcon = step.icon;
                        const isActive = index === currentStep;
                        const isCompleted = index < currentStep;
                        const isDisabled = index > currentStep;
                        return (
                            <React.Fragment key={step.id}>
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
                                        isCompleted ? 'bg-green-600 text-white' :
                                        isActive ? 'bg-blue-600 text-white' :
                                        'bg-gray-100 text-gray-400'
                                    }`}>
                                        {isCompleted ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <p className={`text-sm font-medium ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'}`}>
                                            Step {index + 1}
                                        </p>
                                        <p className={`text-xs ${isActive || isCompleted ? 'text-gray-700' : 'text-gray-400'}`}>
                                            {step.label}
                                        </p>
                                    </div>
                                </div>
                                {index < STEPS.length - 1 && (
                                    <div className={`flex-1 h-0.5 mx-4 ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Messages */}
            {showSuccessMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-800">Client created successfully!</p>
                </div>
            )}
            {showErrorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
            )}

            {/* ==================== STEP 1: Client Details ==================== */}
            {currentStep === 0 && (
                <>
                    {/* Client Type Selection */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Type</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => handleInputChange('clientType', 'RETAIL')}
                                className={`flex items-center justify-center gap-3 p-4 border-2 rounded-lg transition ${
                                    formData.clientType === 'RETAIL'
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                <User className="w-6 h-6" />
                                <div className="text-left">
                                    <p className="font-medium">Retail Client</p>
                                    <p className="text-sm opacity-75">Individual person</p>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleInputChange('clientType', 'CORPORATE')}
                                className={`flex items-center justify-center gap-3 p-4 border-2 rounded-lg transition ${
                                    formData.clientType === 'CORPORATE'
                                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                                        : 'border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                <Building className="w-6 h-6" />
                                <div className="text-left">
                                    <p className="font-medium">Corporate Client</p>
                                    <p className="text-sm opacity-75">Business entity</p>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Tabs and Form */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="border-b">
                            <nav className="flex -mb-px">
                                {['basic', 'contact', ...(formData.clientType === 'CORPORATE' ? ['corporate'] : []), 'settings'].map(tab => {
                                    const tabHasErrors = Object.keys(errors).some(field => getFieldTab(field) === tab);
                                    return (
                                        <button
                                            key={tab}
                                            type="button"
                                            onClick={() => setActiveTab(tab)}
                                            className={`px-6 py-3 text-sm font-medium border-b-2 transition relative ${
                                                activeTab === tab
                                                    ? 'border-blue-500 text-blue-600'
                                                    : tabHasErrors
                                                        ? 'border-transparent text-red-500 hover:text-red-600'
                                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                            }`}
                                        >
                                            {tab === 'basic' ? 'Basic Information' : tab === 'contact' ? 'Contact Details' : tab === 'corporate' ? 'Corporate Details' : 'Settings'}
                                            {tabHasErrors && <span className="ml-1.5 inline-block w-2 h-2 bg-red-500 rounded-full" />}
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>

                        <div className="p-6">
                            {/* Basic Information Tab */}
                            {activeTab === 'basic' && (
                                <div className="space-y-6">
                                    {formData.clientType === 'RETAIL' ? (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        <User className="w-4 h-4 inline mr-1" />
                                                        First Name *
                                                    </label>
                                                    <input type="text" value={formData.firstName} onChange={(e) => handleInputChange('firstName', e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                                                        placeholder="Enter first name" />
                                                    {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Middle Name</label>
                                                    <input type="text" value={formData.middleName} onChange={(e) => handleInputChange('middleName', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter middle name" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                                                    <input type="text" value={formData.lastName} onChange={(e) => handleInputChange('lastName', e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                                                        placeholder="Enter last name" />
                                                    {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        <Calendar className="w-4 h-4 inline mr-1" />Date of Birth
                                                    </label>
                                                    <input type="date" value={formData.dateOfBirth} onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer relative" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
                                                    <CustomSelect value={formData.gender} onChange={(val) => handleInputChange('gender', val)}
                                                        options={[{ value: 'MALE', label: 'Male' }, { value: 'FEMALE', label: 'Female' }]}
                                                        placeholder="Select gender" error={!!errors.gender} />
                                                    {errors.gender && <p className="mt-1 text-sm text-red-600">{errors.gender}</p>}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        <CreditCard className="w-4 h-4 inline mr-1" />National ID
                                                    </label>
                                                    <input type="text" value={formData.nationalId} onChange={(e) => handleInputChange('nationalId', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter national ID" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        <FileText className="w-4 h-4 inline mr-1" />KRA PIN
                                                    </label>
                                                    <input type="text" value={formData.kraPin} onChange={(e) => handleInputChange('kraPin', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. A012345678Z" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        <Globe className="w-4 h-4 inline mr-1" />Passport Number
                                                    </label>
                                                    <input type="text" value={formData.passportNumber} onChange={(e) => handleInputChange('passportNumber', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter passport number" />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <Building className="w-4 h-4 inline mr-1" />Company Name *
                                                </label>
                                                <input type="text" value={formData.companyName} onChange={(e) => handleInputChange('companyName', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.companyName ? 'border-red-500' : 'border-gray-300'}`}
                                                    placeholder="Enter company name" />
                                                {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <Briefcase className="w-4 h-4 inline mr-1" />Industry
                                                </label>
                                                <input type="text" value={formData.industry} onChange={(e) => handleInputChange('industry', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="e.g. Technology, Finance, Healthcare" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Contact Details Tab */}
                            {activeTab === 'contact' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <Mail className="w-4 h-4 inline mr-1" />Primary Email *
                                            </label>
                                            <input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="example@email.com" />
                                            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <Mail className="w-4 h-4 inline mr-1" />Secondary Email
                                            </label>
                                            <input type="email" value={formData.email2} onChange={(e) => handleInputChange('email2', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="alternative@email.com" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <Phone className="w-4 h-4 inline mr-1" />Primary Phone
                                            </label>
                                            <input type="tel" value={formData.phoneNumber} onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="+254 700 000 000" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <Phone className="w-4 h-4 inline mr-1" />Secondary Phone
                                            </label>
                                            <input type="tel" value={formData.phoneNumber2} onChange={(e) => handleInputChange('phoneNumber2', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="+254 700 000 000" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            <MapPin className="w-4 h-4 inline mr-1" />Address
                                        </label>
                                        <textarea value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)} rows="3"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter full address" />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <Globe className="w-4 h-4 inline mr-1" />City
                                            </label>
                                            <input type="text" value={formData.city} onChange={(e) => handleInputChange('city', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. Nairobi" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <Globe className="w-4 h-4 inline mr-1" />Country
                                            </label>
                                            <input type="text" value={formData.country} onChange={(e) => handleInputChange('country', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. Kenya" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Corporate Details Tab */}
                            {activeTab === 'corporate' && formData.clientType === 'CORPORATE' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <FileText className="w-4 h-4 inline mr-1" />Registration Number *
                                            </label>
                                            <input type="text" value={formData.registrationNumber} onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.registrationNumber ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="Company registration number" />
                                            {errors.registrationNumber && <p className="mt-1 text-sm text-red-600">{errors.registrationNumber}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                <CreditCard className="w-4 h-4 inline mr-1" />Tax ID Number
                                            </label>
                                            <input type="text" value={formData.taxIdNumber} onChange={(e) => handleInputChange('taxIdNumber', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Tax identification number" />
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-4">Contact Person Details</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <UserCheck className="w-4 h-4 inline mr-1" />Contact Person Name *
                                                </label>
                                                <input type="text" value={formData.contactPersonName} onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.contactPersonName ? 'border-red-500' : 'border-gray-300'}`}
                                                    placeholder="Full name" />
                                                {errors.contactPersonName && <p className="mt-1 text-sm text-red-600">{errors.contactPersonName}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <Mail className="w-4 h-4 inline mr-1" />Contact Person Email *
                                                </label>
                                                <input type="email" value={formData.contactPersonEmail} onChange={(e) => handleInputChange('contactPersonEmail', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.contactPersonEmail ? 'border-red-500' : 'border-gray-300'}`}
                                                    placeholder="contact@company.com" />
                                                {errors.contactPersonEmail && <p className="mt-1 text-sm text-red-600">{errors.contactPersonEmail}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    <Phone className="w-4 h-4 inline mr-1" />Contact Person Phone
                                                </label>
                                                <input type="tel" value={formData.contactPersonPhone} onChange={(e) => handleInputChange('contactPersonPhone', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    placeholder="+254 700 000 000" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Settings Tab */}
                            {activeTab === 'settings' && (
                                <div className="space-y-6">
                                    <div className="bg-gray-50 p-6 rounded-lg">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Preferences</h3>
                                        <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-blue-100 rounded-lg">
                                                    <Mail className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">Cross-Selling Consent</p>
                                                    <p className="text-sm text-gray-500">Allow us to send promotional offers and product updates</p>
                                                </div>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input type="checkbox" checked={formData.crossSellingConsent} onChange={(e) => handleInputChange('crossSellingConsent', e.target.checked)} className="sr-only peer" />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Step 1 Footer */}
                        <div className="border-t p-4 flex items-center justify-between">
                            <button type="button" onClick={() => navigate('/clients')}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                                Cancel
                            </button>
                            <button type="button" onClick={handleCreateClient} disabled={loading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2">
                                {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Creating...</> :
                                    <><Save className="w-4 h-4" />Create & Continue<ArrowRight className="w-4 h-4" /></>}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ==================== STEP 2: Select Company ==================== */}
            {currentStep === 1 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Select Company</h3>
                        <p className="text-sm text-gray-500 mb-6">Choose the company to set up KYC requirements for</p>

                        {loadingCompanies ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {companies.map(company => (
                                    <button key={company.id} type="button"
                                        onClick={() => setSelectedCompanyId(company.id)}
                                        className={`text-left p-4 border-2 rounded-lg transition ${
                                            selectedCompanyId === company.id
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                        }`}>
                                        <div className="flex items-start gap-3">
                                            <div className={`p-2 rounded-lg ${selectedCompanyId === company.id ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                                <Building2 className={`w-5 h-5 ${selectedCompanyId === company.id ? 'text-blue-600' : 'text-gray-500'}`} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className={`font-medium truncate ${selectedCompanyId === company.id ? 'text-blue-900' : 'text-gray-900'}`}>
                                                    {company.name}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">{company.businessScope}</p>
                                                {company.code && <p className="text-xs text-gray-400 mt-0.5">{company.code}</p>}
                                            </div>
                                            {selectedCompanyId === company.id && (
                                                <Check className="w-5 h-5 text-blue-600 flex-shrink-0 ml-auto" />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-t p-4 flex items-center justify-between">
                        <button type="button" onClick={() => {
                            navigate(`/clients/${createdClient.id}`);
                        }} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                            Skip KYC for Now
                        </button>
                        <button type="button" onClick={() => setCurrentStep(2)} disabled={!selectedCompanyId}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2">
                            Continue <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* ==================== STEP 3: KYC Checklist ==================== */}
            {currentStep === 2 && (
                <div className="space-y-4">
                    {/* KYC Messages */}
                    {kycSuccess && (
                        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> {kycSuccess}
                        </div>
                    )}
                    {kycError && (
                        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" /> {kycError}
                            <button onClick={() => setKycError('')} className="ml-auto"><X className="w-4 h-4" /></button>
                        </div>
                    )}

                    {loadingChecklist ? (
                        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : checklist ? (
                        <>
                            {/* Progress Summary */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">KYC Checklist</h3>
                                        <p className="text-sm text-gray-500">{checklist.companyName} &middot; {checklist.clientType}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold text-gray-900">
                                            {checklist.mandatoryApproved}/{checklist.mandatoryTotal}
                                        </p>
                                        <p className="text-xs text-gray-500">mandatory approved</p>
                                    </div>
                                </div>
                                {checklist.mandatoryTotal > 0 && (
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all ${checklist.complete ? 'bg-green-600' : 'bg-blue-600'}`}
                                            style={{ width: `${Math.round((checklist.mandatoryApproved / checklist.mandatoryTotal) * 100)}%` }}
                                        />
                                    </div>
                                )}
                                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> {checklist.mandatoryApproved} approved</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> {checklist.mandatoryPending} pending</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" /> {checklist.mandatoryMissing} missing</span>
                                    {checklist.optionalTotal > 0 && (
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-300" /> {checklist.optionalSubmitted}/{checklist.optionalTotal} optional</span>
                                    )}
                                </div>
                            </div>

                            {/* Standard KYC Section */}
                            {checklist.standardKyc && checklist.standardKyc.length > 0 && (
                                <ChecklistSection
                                    title="Standard KYC"
                                    icon={<Shield className="w-5 h-5 text-blue-600" />}
                                    items={checklist.standardKyc}
                                    uploadingItem={uploadingItem}
                                    uploadForm={uploadForm}
                                    setUploadForm={setUploadForm}
                                    dragging={dragging}
                                    setDragging={setDragging}
                                    fileInputRef={fileInputRef}
                                    submittingKyc={submittingKyc}
                                    onOpenUpload={openUploadForItem}
                                    onSubmit={handleKycSubmit}
                                    validateAndSetFile={validateAndSetFile}
                                />
                            )}

                            {/* Beneficial Ownership Section */}
                            {checklist.beneficialOwnership && checklist.beneficialOwnership.length > 0 && (
                                <ChecklistSection
                                    title="Beneficial Ownership"
                                    icon={<Building className="w-5 h-5 text-amber-600" />}
                                    items={checklist.beneficialOwnership}
                                    uploadingItem={uploadingItem}
                                    uploadForm={uploadForm}
                                    setUploadForm={setUploadForm}
                                    dragging={dragging}
                                    setDragging={setDragging}
                                    fileInputRef={fileInputRef}
                                    submittingKyc={submittingKyc}
                                    onOpenUpload={openUploadForItem}
                                    onSubmit={handleKycSubmit}
                                    validateAndSetFile={validateAndSetFile}
                                />
                            )}
                        </>
                    ) : null}

                    {/* Step 3 Footer */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
                        <button type="button" onClick={() => setCurrentStep(1)}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition flex items-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                        <button type="button" onClick={() => navigate(`/clients/${createdClient.id}`)}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2">
                            <Check className="w-4 h-4" /> Finish & View Client
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ==================== Checklist Section Component ==================== */

const organizeItems = (items) => {
    if (!items || items.length === 0) return [];
    const result = [];
    const seenGroups = new Set();
    for (const item of items) {
        if (item.groupName) {
            if (!seenGroups.has(item.groupName)) {
                seenGroups.add(item.groupName);
                const groupItems = items.filter(i => i.groupName === item.groupName);
                result.push({
                    type: 'group',
                    groupName: item.groupName,
                    groupSatisfied: groupItems[0]?.groupSatisfied || false,
                    items: groupItems,
                });
            }
        } else {
            result.push({ type: 'standalone', item });
        }
    }
    return result;
};

const formatGroupName = (name) => {
    if (!name) return '';
    return name.replace(/_/g, ' ').toLowerCase()
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

const ChecklistSection = ({ title, icon, items, uploadingItem, uploadForm, setUploadForm, dragging, setDragging, fileInputRef, submittingKyc, onOpenUpload, onSubmit, validateAndSetFile }) => {
    const organized = organizeItems(items);

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
                {icon}
                <h4 className="font-semibold text-gray-900">{title}</h4>
                <span className="text-xs text-gray-400 ml-1">({items.length})</span>
            </div>
            <div className="divide-y divide-gray-100">
                {organized.map((entry, idx) => {
                    if (entry.type === 'group') {
                        return (
                            <div key={entry.groupName}>
                                {/* Group header */}
                                <div className="px-6 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-gray-700">
                                            {formatGroupName(entry.groupName)}
                                        </span>
                                        <span className="text-[10px] text-gray-400 italic">— submit any one</span>
                                    </div>
                                    {entry.groupSatisfied ? (
                                        <span className="flex items-center gap-1 text-[10px] font-medium text-green-600">
                                            <CheckCircle className="w-3 h-3" />
                                            Met
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1 text-[10px] font-medium text-red-500">
                                            <AlertCircle className="w-3 h-3" />
                                            Missing
                                        </span>
                                    )}
                                </div>
                                {/* Grouped items */}
                                {entry.items.map(item => (
                                    <ChecklistItem
                                        key={item.requirementId}
                                        item={item}
                                        isUploading={uploadingItem === item.requirementId}
                                        uploadForm={uploadForm}
                                        setUploadForm={setUploadForm}
                                        dragging={dragging}
                                        setDragging={setDragging}
                                        fileInputRef={fileInputRef}
                                        submittingKyc={submittingKyc}
                                        onOpenUpload={onOpenUpload}
                                        onSubmit={onSubmit}
                                        validateAndSetFile={validateAndSetFile}
                                        indented
                                    />
                                ))}
                            </div>
                        );
                    }
                    return (
                        <ChecklistItem
                            key={entry.item.requirementId}
                            item={entry.item}
                            isUploading={uploadingItem === entry.item.requirementId}
                            uploadForm={uploadForm}
                            setUploadForm={setUploadForm}
                            dragging={dragging}
                            setDragging={setDragging}
                            fileInputRef={fileInputRef}
                            submittingKyc={submittingKyc}
                            onOpenUpload={onOpenUpload}
                            onSubmit={onSubmit}
                            validateAndSetFile={validateAndSetFile}
                        />
                    );
                })}
            </div>
        </div>
    );
};

/* ==================== Checklist Item Component ==================== */

const ChecklistItem = ({ item, isUploading, uploadForm, setUploadForm, dragging, setDragging, fileInputRef, submittingKyc, onOpenUpload, onSubmit, validateAndSetFile, indented }) => {
    const hasSubmission = item.submissionId != null;
    const statusConfig = hasSubmission && STATUS_CONFIG[item.status] ? STATUS_CONFIG[item.status] : null;
    const StatusIcon = statusConfig?.icon;

    return (
        <div className={`${indented ? 'pl-9 pr-6' : 'px-6'} py-3`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                    {/* Status indicator */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        hasSubmission && item.status === 'APPROVED' ? 'bg-green-100' :
                        hasSubmission ? 'bg-yellow-100' :
                        item.mandatory ? 'bg-red-50' : 'bg-gray-50'
                    }`}>
                        {hasSubmission && item.status === 'APPROVED' ? <CheckCircle className="w-4 h-4 text-green-600" /> :
                         hasSubmission ? <Clock className="w-4 h-4 text-yellow-600" /> :
                         item.mandatory ? <AlertCircle className="w-4 h-4 text-red-400" /> :
                         <FileText className="w-4 h-4 text-gray-400" />}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{item.requirementName}</p>
                            {item.mandatory ? (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded">Required</span>
                            ) : (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded">Optional</span>
                            )}
                        </div>
                        {item.description && <p className="text-xs text-gray-400 truncate">{item.description}</p>}
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Status badge */}
                    {hasSubmission && statusConfig && (
                        <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label} v{item.version}
                        </span>
                    )}

                    {/* Upload button */}
                    {(!hasSubmission || item.status === 'REJECTED') && (
                        <button onClick={() => onOpenUpload(item.requirementId, item.type)}
                            className={`px-3 py-1.5 text-xs rounded-lg transition flex items-center gap-1.5 ${
                                isUploading
                                    ? 'bg-gray-100 text-gray-700'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}>
                            <Upload className="w-3.5 h-3.5" />
                            {hasSubmission ? 'Resubmit' : 'Upload'}
                        </button>
                    )}
                </div>
            </div>

            {/* Inline upload form */}
            {isUploading && (
                <div className="mt-3 ml-11 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Document Number</label>
                            <input type="text" value={uploadForm.documentNumber}
                                onChange={(e) => setUploadForm(prev => ({ ...prev, documentNumber: e.target.value }))}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., 12345678" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                            <input type="text" value={uploadForm.notes}
                                onChange={(e) => setUploadForm(prev => ({ ...prev, notes: e.target.value }))}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Optional notes..." />
                        </div>
                    </div>

                    {/* File drop zone */}
                    <div
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); validateAndSetFile(e.dataTransfer.files[0]); }}
                        className={`border-2 border-dashed rounded-lg p-4 text-center transition ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                    >
                        {uploadForm.file ? (
                            <div className="flex items-center justify-center gap-3">
                                <FileText className="w-5 h-5 text-blue-600" />
                                <span className="text-sm text-gray-900 font-medium">{uploadForm.file.name}</span>
                                <span className="text-xs text-gray-500">({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                <button type="button" onClick={() => setUploadForm(prev => ({ ...prev, file: null }))}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <div onClick={() => fileInputRef.current?.click()} className="cursor-pointer">
                                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => validateAndSetFile(e.target.files[0])} className="hidden" />
                                <Upload className={`w-8 h-8 mx-auto ${dragging ? 'text-blue-500' : 'text-gray-400'}`} />
                                <p className="text-xs text-gray-600 mt-1">{dragging ? 'Drop your file here' : 'Click to upload or drag and drop'}</p>
                                <p className="text-[10px] text-gray-400">PDF, JPG, PNG (max 10MB)</p>
                            </div>
                        )}
                    </div>

                    {/* Upload actions */}
                    <div className="flex items-center gap-2">
                        <button onClick={() => onSubmit(item.requirementId)}
                            disabled={submittingKyc || !uploadForm.file}
                            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-1.5">
                            {submittingKyc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            Submit
                        </button>
                        <button onClick={() => onOpenUpload(null)}
                            className="px-4 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientOnboardingWizard;
